/**
 * Der Weg des Konzepts in fünf Schritten — für den Überblick im Fenster.
 *
 * Das Fenster zeigte bisher einen Statussatz, einen Balken und darunter
 * fünf Abschnitte mit Zahlen. Was davon erledigt ist, ließ sich daraus
 * zusammenreimen; wer als Nächstes am Zug ist, nicht. Diese Datei macht aus
 * denselben Zahlen eine Reihenfolge: fünf Schritte, jeder mit Stand, Zahl und
 * der Rolle, bei der er liegt.
 *
 * **Gerechnet wird hier nichts.** Die Zahlen kommen aus `progress.stages`
 * (Server, `services/concept/progress.js`) und aus `state` — dieselben Werte,
 * die den Balken füllen und die Sperre entscheiden. Diese Datei ordnet sie an
 * und schreibt Sätze dazu. Rechnete sie selbst, gäbe es eine zweite Wahrheit
 * neben dem Balken darüber.
 *
 * **Warum fünf und nicht vier.** Der Balken hat vier Abschnitte: Lernstrecke,
 * Konzeptfelder, Zuteilung, Bewertung. Dazwischen liegt aber ein Schritt, den
 * niemand sieht und an dem es am häufigsten hängt: Die Schüler:innen müssen das
 * Konzept **abschließen**, sonst kann die Lehrkraft gar nicht bewerten. Als
 * eigener Schritt steht er da, wo er hingehört — er zählt nur nicht in die
 * Prozentzahl hinein, weil er dort nie gezählt hat.
 */

const CONCEPT_STATUS = {
	DRAFT: 'DRAFT',
	SUBMITTED: 'SUBMITTED',
	RETURNED: 'RETURNED',
	SCORED: 'SCORED'
}

/** „1 Konzeptfeld" gegen „3 Konzeptfelder" — i18next 20 kennt `_one` noch nicht. */
const zahlwort = (anzahl, eines, mehrere) => `${anzahl} ${anzahl === 1 ? eines : mehrere}`

/** Namen aufzählen, ab dem vierten nur noch zählen. */
const namen = liste => {
	const alle = (liste || []).map(eintrag => eintrag.name || 'unbekannt')
	if (alle.length <= 3) return alle.join(', ')

	return `${alle.slice(0, 3).join(', ')} und ${alle.length - 3} weitere`
}

const stufe = (progress, key) => (progress?.stages || []).find(eintrag => eintrag.key === key) || null

/**
 * Die fünf Schritte mit ihrem Stand.
 *
 * @param progress `{ percent, stages }` aus der Antwort — oder `null`.
 * @param state    Der `state`-Teil derselben Antwort (`evaluateConcept`).
 * @param status   Der Stand des Konzepts (`DRAFT` … `SCORED`).
 *
 * @returns `{ steps, next }` — die Schritte in ihrer Reihenfolge und der Satz
 *          dazu, was als Nächstes zu tun ist und von wem. `steps` ist leer,
 *          wenn der Stand fehlt: Fünf Schritte auf „offen" wären eine Aussage,
 *          die niemand geprüft hat.
 */
export const conceptSteps = ({ progress, state, status } = {}) => {
	if (!progress || !state || state.applies === false) return { steps: [], next: '' }

	const kurs = stufe(progress, 'COURSE')
	const felder = stufe(progress, 'FIELDS')
	const zuteilung = stufe(progress, 'ASSIGNMENT')
	const bewertung = stufe(progress, 'ASSESSMENT')

	if (!kurs || !felder || !zuteilung || !bewertung) return { steps: [], next: '' }

	const zurueck = status === CONCEPT_STATUS.RETURNED
	// Der Abschluss ist getan, sobald das Konzept bei der Lehrkraft liegt. Eine
	// Rückgabe nimmt ihn zurück — dann ist er wieder offen, und zwar bei den
	// Schüler:innen.
	const abgeschlossen = !!state.submitted && !zurueck

	const offeneFelder = Math.max(0, (felder.total || 0) - (felder.done || 0))
	const bewertet = Number(state.fieldsScored) || 0
	const felderGesamt = felder.total || 0

	const steps = [
		{
			key: 'COURSE',
			label: 'Lernstrecke',
			who: 'Schüler:innen',
			done: kurs.complete,
			// Verlangt die Einstellung keinen Abschluss, steht der Schritt auf
			// erledigt — mit dem Grund dazu, sonst läse sich „erledigt" als
			// „alle haben bestanden".
			detail: kurs.total
				? kurs.total === 1
					? `${kurs.done} von einem nötigen Abschluss`
					: `${kurs.done} von ${kurs.total} nötigen Abschlüssen`
				: 'für diese Unternehmensform nicht verlangt'
		},
		{
			key: 'FIELDS',
			label: 'Konzeptfelder schreiben',
			who: 'Junior Company',
			done: felder.complete,
			detail: `${felder.done} von ${felderGesamt} Feldern befüllt`
		},
		{
			key: 'ASSIGNMENT',
			label: 'Felder unter den Schüler:innen aufteilen',
			who: 'Junior Company',
			done: zuteilung.complete,
			detail: zuteilung.total
				? `${zuteilung.done} von ${zahlwort(zuteilung.total, 'Schüler:in', 'Schüler:innen')} zugeteilt`
				: 'noch keine Schüler:innen in der Junior Company'
		},
		{
			key: 'SUBMIT',
			label: 'Konzept zur Bewertung abschließen',
			who: 'Schüler:innen',
			done: abgeschlossen,
			detail: zurueck
				? 'zur Überarbeitung zurückgegeben'
				: abgeschlossen
				? 'liegt bei der Lehrkraft'
				: state.canSubmit
				? 'kann jetzt abgeschlossen werden'
				: 'möglich, sobald die drei Schritte darüber stehen'
		},
		{
			key: 'ASSESSMENT',
			label: 'Bewertung',
			who: 'betreuende Lehrkraft',
			done: bewertung.complete,
			detail: `${bewertet} von ${felderGesamt} Feldern bewertet`
		}
	]

	// Der erste offene Schritt ist der, an dem es hängt — alles danach wartet
	// auf ihn.
	const offen = steps.find(schritt => !schritt.done)
	steps.forEach(schritt => {
		schritt.current = !!offen && schritt.key === offen.key
	})

	return { steps, next: naechsterSatz({ offen, state, status, offeneFelder, felderGesamt, bewertet }) }
}

/** Ein Satz: was zu tun ist und von wem. */
const naechsterSatz = ({ offen, state, status, offeneFelder, felderGesamt, bewertet }) => {
	if (!offen) {
		return state.waived
			? 'Nichts offen — das Konzept ist bewertet. Zusätzlich wurde es im Störfall freigegeben.'
			: 'Nichts offen — das Konzept ist bewertet.'
	}

	switch (offen.key) {
		case 'COURSE': {
			const fehlt = Number(state.courseMissing) || 0

			return state.courseOpen?.length && fehlt
				? `Die Schüler:innen schließen die Lernstrecke ab — es ${
						fehlt === 1 ? 'fehlt noch ein Abschluss' : `fehlen noch ${fehlt} Abschlüsse`
				  }. Offen bei: ${namen(state.courseOpen)}.`
				: 'Die Schüler:innen schließen die Lernstrecke ab.'
		}

		case 'FIELDS':
			return `Die Junior Company schreibt ${
				offeneFelder === felderGesamt
					? `die ${felderGesamt} Konzeptfelder`
					: offeneFelder === 1
					? 'das letzte Konzeptfeld'
					: `die restlichen ${offeneFelder} Konzeptfelder`
			}.`

		case 'ASSIGNMENT':
			return state.unassigned?.length
				? `Jede:r Schüler:in muss mindestens einem Konzeptfeld zugeteilt sein — offen: ${namen(
						state.unassigned
				  )}.`
				: 'Die Junior Company teilt die Konzeptfelder unter ihren Schüler:innen auf.'

		case 'SUBMIT':
			if (status === CONCEPT_STATUS.RETURNED) {
				return 'Die Schüler:innen überarbeiten das Konzept und schließen es erneut ab.'
			}

			return state.canSubmit
				? 'Die Schüler:innen schließen das Konzept zur Bewertung ab — alles Nötige steht.'
				: 'Die Schüler:innen schließen das Konzept ab, sobald die Schritte darüber erledigt sind.'

		case 'ASSESSMENT':
			// Nach einer Rückgabe stehen die Punkte von zuvor schon da. „15 von 15
			// bewertet, Bewertung steht aus" wäre ohne diesen Zusatz ein Rätsel.
			return state.allFieldsScored
				? 'Die betreuende Lehrkraft bestätigt oder ändert die Punkte von zuvor und schließt die Bewertung ab.'
				: `Die betreuende Lehrkraft bewertet die ${felderGesamt} Konzeptfelder — ${
						// „0 davon sind bewertet" ist richtig gezählt und falsch gesprochen.
						bewertet === 0
							? 'bisher ist keines bewertet'
							: bewertet === 1
							? 'eines davon ist bewertet'
							: `${bewertet} davon sind bewertet`
				  }.`

		default:
			return ''
	}
}
